/*
  Два окна плашки Б1: снять отметку «придержан» и отметить месяцы дела УК.

  Обе правки решают, что уйдёт в суд, поэтому обе требуют причины - она уходит
  в аудит-след. Чек-лист формы (скилл frontend, правило 10): ошибка живёт
  внутри окна, успех закрывает окно и обновляет плашку, кнопка называется по
  тому, что сделает.
*/

import { useState } from 'react'

import { Field, Notice } from './Common.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

const STATES = {
  judged: 'Взыскано - эти месяцы не пойдут в заявление',
  in_court: 'Ещё в суде - эти месяцы тоже не пойдут в заявление',
  prepared: 'Подача подготовлена, но не сделана',
  order_cancelled: 'Приказ управляющей компании отменён',
}

function useSubmit(action, onDone) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const submit = async (...args) => {
    setBusy(true)
    setError(null)
    try {
      await action(...args)
      onDone()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, submit }
}

export function ClearHoldForm({ hold, onClear, onClose }) {
  const [reason, setReason] = useState('')
  const { busy, error, submit } = useSubmit(onClear, onClose)
  return (
    <Modal title="Снять отметку «придержан»" onClose={onClose} busy={busy}>
      <p className="modal__text">
        Управляющая компания просила подождать: «{hold.reason}». После снятия отметки счёт
        вернётся в очередь к подаче.
      </p>
      <Field label="Почему счёт можно отправлять в работу">
        <textarea
          className="field__input"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <Notice error={error} />
      <div className="row row--tight">
        <button
          className="button"
          type="button"
          disabled={busy || !reason.trim()}
          onClick={() => submit(reason.trim())}
        >
          Снять отметку
        </button>
        <button className="button button--quiet" type="button" onClick={onClose} disabled={busy}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}

export function CaseMonthsForm({ item, onSave, onClose }) {
  const [since, setSince] = useState(item.period_from ?? '')
  const [through, setThrough] = useState(item.period_through ?? '')
  const [state, setState] = useState(item.state === 'in_work' ? 'judged' : item.state)
  const [reason, setReason] = useState('')
  const { busy, error, submit } = useSubmit(
    (months) => onSave(item.id, months),
    onClose,
  )
  const ready = through && reason.trim() && !busy
  return (
    <Modal title="Месяцы дела управляющей компании" onClose={onClose} busy={busy}>
      <p className="modal__text">Отметка реестра: «{item.source_text}».</p>
      <div className="row case-months__dates">
        <Field label="С месяца (пусто - с начала долга)">
          <input
            className="field__input"
            type="month"
            value={since}
            onChange={(event) => setSince(event.target.value)}
          />
        </Field>
        <Field label="По месяц включительно">
          <input
            className="field__input"
            type="month"
            value={through}
            onChange={(event) => setThrough(event.target.value)}
          />
        </Field>
      </div>
      <div className="field">
        <span className="field__label" id="case-state-label">
          Что с делом
        </span>
        <Dropdown value={state} onChange={setState} options={STATES} labelledBy="case-state-label" />
      </div>
      <Field label="Откуда известно (уйдёт в журнал действий)">
        <textarea
          className="field__input"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <Notice error={error} />
      <div className="row row--tight">
        <button
          className="button"
          type="button"
          disabled={!ready}
          onClick={() =>
            submit({
              period_from: since || null,
              period_through: through,
              state,
              reason: reason.trim(),
            })
          }
        >
          Сохранить месяцы
        </button>
        <button className="button button--quiet" type="button" onClick={onClose} disabled={busy}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}

/*
  «Суд вернул заявление» (решение владельца 26.10.2026): дата определения и
  причина обязательны - обе встают в ленту счёта и в журнал действий. После
  возврата месяцы дела снова войдут в заявление, а счёт - в очередь к подаче;
  окно говорит об этом до нажатия, а не после.
*/
/* Сегодня по часам компьютера, а не по Гринвичу: утром по Москве `toISOString`
   давал бы вчерашнюю дату. */
function localToday() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

export function FilingReturnForm({ item, onSave, onClose }) {
  const [returnedOn, setReturnedOn] = useState(localToday)
  const [reason, setReason] = useState('')
  const { busy, error, submit } = useSubmit((facts) => onSave(item.id, facts), onClose)
  const ready = returnedOn && reason.trim() && !busy
  return (
    <Modal title="Суд вернул заявление" onClose={onClose} busy={busy}>
      <p className="modal__text">
        Заявление{item.period ? ` за ${item.period}` : ''} будет закрыто как возвращённое. Его
        месяцы снова войдут в расчёт, а счёт вернётся в очередь к подаче.
      </p>
      <Field label="Дата определения о возврате">
        <input
          className="field__input"
          type="date"
          value={returnedOn}
          onChange={(event) => setReturnedOn(event.target.value)}
        />
      </Field>
      <Field label="Причина возврата (уйдёт в ленту и журнал)">
        <textarea
          className="field__input"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <Notice error={error} />
      <div className="row row--tight">
        <button
          className="button"
          type="button"
          disabled={!ready}
          onClick={() => submit({ returned_on: returnedOn, reason: reason.trim() })}
        >
          Отметить возврат
        </button>
        <button className="button button--quiet" type="button" onClick={onClose} disabled={busy}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}
